import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import PageLayout from '@/components/layout/PageLayout';
import { toast } from '@/hooks/use-toast';
import { SectionHeading } from '@/components/ui/section-heading';
import { supabase } from '@/integrations/supabase/client';
import { Alert, AlertDescription } from '@/components/ui/alert';

const resetPasswordSchema = z.object({
  password: z.string()
    .min(8, { message: 'Password must be at least 8 characters' })
    .regex(/[A-Z]/, { message: 'Password must contain at least one capital letter' })
    .regex(/[0-9]/, { message: 'Password must contain at least one number' })
    .regex(/[^a-zA-Z0-9]/, { message: 'Password must contain at least one special character' }),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  const [validSession, setValidSession] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Verify that the user came from a password reset link
  useEffect(() => {
    const verifyResetSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        // Get the current URL parameters
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const type = params.get('type');

        // Valid if we have a session or a valid recovery flow
        if (session || (code && type === 'recovery')) {
          setValidSession(true);
        } else {
          toast({
            variant: "destructive",
            title: "Invalid or expired session",
            description: "The password reset link has expired. Please request a new one."
          });
        }
      } catch (error) {
        console.error("Error verifying reset session:", error);
        toast({
          variant: "destructive",
          title: "Session verification failed",
          description: "Please try requesting a new password reset link"
        });
      } finally {
        setVerifyingSession(false);
      }
    };
    
    verifyResetSession();
  }, []);

  const onSubmit = async (data: ResetPasswordFormValues) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. Please sign in with your new password."
      });
      
      // Sign out the user and redirect to login
      await supabase.auth.signOut();
      navigate('/auth/login');
      
    } catch (error: any) {
      console.error('Password update error:', error);
      toast({
        variant: "destructive",
        title: "Error updating password",
        description: error.message || 'Failed to update password. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (verifyingSession) {
    return (
      <PageLayout>
        <div className="container max-w-md mx-auto py-16">
          <div className="text-center">
            <p>Verifying your reset link...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!validSession) {
    return (
      <PageLayout>
        <div className="container max-w-md mx-auto py-16">
          <div className="space-y-6">
            <Alert variant="destructive">
              <AlertDescription>
                Invalid or expired password reset link. Please request a new one.
              </AlertDescription>
            </Alert>
            <div className="text-center">
              <Button onClick={() => navigate('/auth/forgot-password')}>
                Request New Reset Link
              </Button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="container max-w-md mx-auto py-16">
        <div className="space-y-6">
          <SectionHeading
            title="Reset Password"
            gradient={true}
          />
          <div className="space-y-2 text-center">
            <p className="text-gray-500">Enter your new password below</p>
            <div className="text-sm text-gray-500">
              <p>Password must contain:</p>
              <ul className="list-disc list-inside">
                <li>At least 8 characters</li>
                <li>At least one capital letter</li>
                <li>At least one number</li>
                <li>At least one special character</li>
              </ul>
            </div>
          </div>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input placeholder="••••••••" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm New Password</FormLabel>
                    <FormControl>
                      <Input placeholder="••••••••" type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Reset Password'}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </PageLayout>
  );
}
